import { call, put, takeLatest } from 'redux-saga/effects';
import { fiscalReports } from 'services/api/fiscalReports';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';
import { Notification } from 'ui/Notification';
import { notificationContainer } from 'utils/notificationContainer';
import axios from 'axios';
import {
	TFiscalVatListInfoFilterRequest,
	TFiscalVatListInfoFilterResponse,
	TFiscalReportListRequest,
	TFiscalReportListResponse,
	TDownloadFiscalPdfRequest,
	TDownloadFiscalPdfResponse,
	TDownloadFiscalCsvRequest,
	TDownloadFiscalCsvResponse,
	TDownloadFiscalXlsxRequest,
	TDownloadFiscalXlsxResponse,
	TExportFiscalXlsxRequest,
	TExportFiscalXlsxResponse,
	TUpdateFiscalReportPaymentRequest,
	TUpdateFiscalReportPaymentResponse,
	TUpdateFiscalReportCommentRequest,
	TUpdateFiscalReportCommentResponse,
	TSendPaidResponse,
	TSendReportRequest,
	TSendReportResponse,
	TCancelReportRequest,
	TCancelReportResponse,
} from './types';
import {
	getFiscalReportListRequest,
	getFiscalReportListSuccess,
	getFiscalVatListInfoFilterRequest,
	getFiscalVatListInfoFilterFailure,
	getFiscalVatListInfoFilterSuccess,
	getFiscalReportListFailure,
	downloadFiscalPdfRequest,
	downloadFiscalPdfSuccess,
	downloadFiscalPdfFailure,
	downloadFiscalCsvRequest,
	downloadFiscalCsvSuccess,
	downloadFiscalCsvFailure,
	downloadFiscalXlsxRequest,
	downloadFiscalXlsxSuccess,
	downloadFiscalXlsxFailure,
	exportFiscalXlsxRequest,
	exportFiscalXlsxSuccess,
	exportFiscalXlsxFailure,
	updateFiscalReportPaymentRequest,
	updateFiscalReportPaymentSuccess,
	updateFiscalReportPaymentFailure,
	updateFiscalReportCommentRequest,
	updateFiscalReportCommentSuccess,
	updateFiscalReportCommentFailure,
	sendPaidRequest,
	sendPaidSuccess,
	sendPaidFailure,
	sendReportRequest,
	sendReportSuccess,
	sendReportFailure,
	cancelReportRequest,
	cancelReportSuccess,
	cancelReportFailure,
} from './reducer';
import { EReportType, EReportTypeDownload } from './constants';

function* getFiscalVatListInfoFilter(action: PayloadAction<TFiscalVatListInfoFilterRequest>) {
	try {
		const response: TFiscalVatListInfoFilterResponse = yield call(
			fiscalReports.getFiscalVatListInfoFilter,
			action.payload,
		);
		yield put(getFiscalVatListInfoFilterSuccess(response));
	} catch (error) {
		yield put(getFiscalVatListInfoFilterFailure());
		console.error('Error fetching fiscal VAT list info filter:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getFiscalReportList(action: PayloadAction<TFiscalReportListRequest>) {
	try {
		const response: TFiscalReportListResponse = yield call(
			fiscalReports.getFiscalReportList,
			action.payload,
		);
		yield put(getFiscalReportListSuccess(response));
	} catch (error) {
		yield put(getFiscalReportListFailure());
		console.error('Error fetching fiscal report list:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* downloadFiscalPdf(action: PayloadAction<TDownloadFiscalPdfRequest>) {
	try {
		const response: TDownloadFiscalPdfResponse = yield call(
			fiscalReports.downloadFiscalPdf,
			action.payload,
		);
		if (response) {
			fileDownload(
				response,
				`fiscal_report_${String(action.payload.emId)}_${String(action.payload.month)}_${String(
					action.payload.year,
				)}.pdf`,
			);
		}

		yield put(downloadFiscalPdfSuccess(response));
	} catch (error) {
		yield put(downloadFiscalPdfFailure());
		console.error('Error downloading fiscal PDF:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* downloadFiscalCsv(action: PayloadAction<TDownloadFiscalCsvRequest>) {
	try {
		const response: TDownloadFiscalCsvResponse = yield call(fiscalReports.downloadFiscalCsv, {
			...action.payload,
		});
		if (response) {
			const type =
				action.payload.type === 'csv'
					? action.payload.countryCode === 'DE'
						? 'csv'
						: 'zip'
					: action.payload.type;
			fileDownload(
				response,
				`fiscal_report_${String(action.payload.emId)}_${String(action.payload.month)}_${String(
					action.payload.year,
				)}_${String(action.payload.scheme)}_${String(action.payload.countryCode)}.${String(type)}`,
			);
		}

		yield put(downloadFiscalCsvSuccess(response));
	} catch (error) {
		yield put(downloadFiscalCsvFailure());
		console.error('Error downloading fiscal CSV:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* downloadFiscalXlsx(action: PayloadAction<TDownloadFiscalXlsxRequest>) {
	try {
		const response: TDownloadFiscalXlsxResponse = yield call(
			fiscalReports.downloadFiscalXlsx,
			action.payload,
		);
		if (response) {
			fileDownload(
				response,
				`fiscal_report_${String(action.payload.emId)}_${String(action.payload.month)}_${String(
					action.payload.year,
				)}_${String(action.payload.scheme)}.xlsx`,
			);
		}

		yield put(downloadFiscalXlsxSuccess(response));
	} catch (error) {
		yield put(downloadFiscalXlsxFailure());
		console.error('Error downloading fiscal XLSX:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateFiscalReportPayment(action: PayloadAction<TUpdateFiscalReportPaymentRequest>) {
	try {
		const response: TUpdateFiscalReportPaymentResponse = yield call(
			fiscalReports.updateFiscalReportPayment,
			action.payload.body,
		);
		yield put(getFiscalReportListRequest(action.payload.fiscalReportListRequest));
		yield put(updateFiscalReportPaymentSuccess(response));
	} catch (error) {
		yield put(updateFiscalReportPaymentFailure());
		console.error('Error updating fiscal report payment:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateFiscalReportComment(action: PayloadAction<TUpdateFiscalReportCommentRequest>) {
	try {
		const response: TUpdateFiscalReportCommentResponse = yield call(
			fiscalReports.updateFiscalReportComment,
			action.payload.body,
		);
		yield put(getFiscalReportListRequest(action.payload.fiscalReportListRequest));
		yield put(updateFiscalReportCommentSuccess(response));
	} catch (error) {
		yield put(updateFiscalReportCommentFailure());
		console.error('Error updating fiscal report comment:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* exportFiscalXlsx(action: PayloadAction<TExportFiscalXlsxRequest>) {
	try {
		const response: TExportFiscalXlsxResponse = yield call(
			fiscalReports.exportFiscalXlsx,
			action.payload,
		);
		if (response) {
			// Create filename with filter information
			const filterParts: string[] = [];

			action.payload.filter.forEach(filter => {
				const value = String(filter.value);

				if (filter.name === 'timestamp' && filter.comparator === '>=') {
					const date = new Date(value).toISOString().split('T')[0];
					filterParts.push(`from_${date}`);
				} else if (filter.name === 'timestamp' && filter.comparator === '<=') {
					const date = new Date(value).toISOString().split('T')[0];
					filterParts.push(`to_${date}`);
				} else if (filter.name === 'report_type') {
					filterParts.push(`type_${value}`);
				} else if (filter.name === 'payment_handling_mode') {
					filterParts.push(`payment_${value}`);
				} else if (filter.name === 'status') {
					filterParts.push(`status_${value}`);
				} else if (filter.name === 'client') {
					filterParts.push(`client_${value}`);
				} else if (filter.name === 'em_id') {
					filterParts.push(`em_${value}`);
				} else {
					filterParts.push(`${String(filter.name)}_${value}`);
				}
			});

			const filterString = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
			const currentDate = new Date().toISOString().split('T')[0];

			fileDownload(response, `fiscal_reports_export_${currentDate}${filterString}.xlsx`);
		}

		yield put(exportFiscalXlsxSuccess(response));
	} catch (error) {
		yield put(exportFiscalXlsxFailure());
		console.error('Error exporting fiscal XLSX:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* sendPaid() {
	try {
		const response: TSendPaidResponse = yield call(fiscalReports.sendPaid);
		yield put(sendPaidSuccess(response));
	} catch (error) {
		yield put(sendPaidFailure());
		console.error('Error sending paid report:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* sendReport(action: PayloadAction<TSendReportRequest>) {
	try {
		console.log('sendReport', action.payload);

		const response: TSendReportResponse = yield call(fiscalReports.sendReport, action.payload);
		yield put(getFiscalReportListRequest(action.payload.fiscalReportListRequest));
		yield put(sendReportSuccess(response));
	} catch (error) {
		yield put(sendReportFailure());
		console.error('Error sending report:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* cancelReport(action: PayloadAction<TCancelReportRequest>) {
	try {
		const response: TCancelReportResponse = yield call(
			fiscalReports.cancelReport,
			action.payload.reportId,
		);
		yield put(getFiscalReportListRequest(action.payload.fiscalReportListRequest));
		yield put(cancelReportSuccess(response));
	} catch (error) {
		yield put(cancelReportFailure());
		console.error('Error canceling report:', error);
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

export function* fiscalReportsSaga() {
	yield takeLatest(getFiscalVatListInfoFilterRequest.type, getFiscalVatListInfoFilter);
	yield takeLatest(getFiscalReportListRequest.type, getFiscalReportList);
	yield takeLatest(downloadFiscalPdfRequest.type, downloadFiscalPdf);
	yield takeLatest(downloadFiscalCsvRequest.type, downloadFiscalCsv);
	yield takeLatest(downloadFiscalXlsxRequest.type, downloadFiscalXlsx);
	yield takeLatest(exportFiscalXlsxRequest.type, exportFiscalXlsx);
	yield takeLatest(updateFiscalReportPaymentRequest.type, updateFiscalReportPayment);
	yield takeLatest(updateFiscalReportCommentRequest.type, updateFiscalReportComment);
	yield takeLatest(sendPaidRequest.type, sendPaid);
	yield takeLatest(sendReportRequest.type, sendReport);
	yield takeLatest(cancelReportRequest.type, cancelReport);
}
